import React, { useState, useCallback, useEffect } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Portal } from '../../primitives/Portal/Portal.es.js';
import { Overlay } from '../Overlay/Overlay.es.js';
import { Fade } from '../Fade/Fade.es.js';
import '../MaestroThemeProvider/MaestroThemeProvider.es.js';
import { ColorMode } from '../MaestroThemeProvider/ColorMode.es.js';
import { forwardRef } from '../../system/forwardRef.es.js';
import { Wrapper, Modal as Modal$1 } from './styled.es.js';
import { ModalContext } from './ModalContext.es.js';
import { ModalFooter, ModalContinueButton, ModalCancelButton } from './ModalFooter.es.js';
import { ModalHeader, ModalCloseButton, ModalTitle, ModalBackButton } from './ModalHeader.es.js';
import { ModalContent } from './ModalContent.es.js';
import { ModalSection } from './ModalSection.es.js';
import { useModal } from './useModal.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const TRANSITION_DURATION = 300;
const ModalComponent = forwardRef((props, ref) => {
  const _a = props, {
    children,
    variant = "base",
    size = "md",
    isCentered = true,
    isOpen = false,
    withOverlay = true,
    onOpen,
    onClose = () => void 0,
    showCloseButton = true,
    closeOnOverlayClick = true,
    isDismissible = true,
    theme: themeMode = "light",
    scrollBehavior = "inside",
    transitionComponent: TransitionComponent = Fade,
    initialFocusRef = null,
    zIndex = 1e3
  } = _a, rest = __objRest(_a, [
    "children",
    "variant",
    "size",
    "isCentered",
    "isOpen",
    "withOverlay",
    "onOpen",
    "onClose",
    "showCloseButton",
    "closeOnOverlayClick",
    "isDismissible",
    "theme",
    "scrollBehavior",
    "transitionComponent",
    "initialFocusRef",
    "zIndex"
  ]);
  const [isMounted, setIsMounted] = useState(false);
  const handleEnter = () => onOpen == null ? void 0 : onOpen();
  const handleExited = () => setIsMounted(false);
  const handleEntered = useCallback(() => {
    var _a2, _b;
    (_b = (_a2 = initialFocusRef == null ? void 0 : initialFocusRef.current) == null ? void 0 : _a2.focus) == null ? void 0 : _b.call(_a2);
  }, [initialFocusRef]);
  const modal = useModal({ isOpen, onClose, closeOnOverlayClick, isDismissible });
  const context = __spreadProps(__spreadValues({}, modal), { onClose, showCloseButton, variant, size, scrollBehavior, isCentered });
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);
  return /* @__PURE__ */ React.createElement(ModalContext.Provider, {
    value: context
  }, isMounted && /* @__PURE__ */ React.createElement(Portal, {
    zIndex
  }, /* @__PURE__ */ React.createElement(ColorMode, {
    mode: themeMode
  }, withOverlay && /* @__PURE__ */ React.createElement(Overlay, {
    isVisible: isOpen,
    timeout: TRANSITION_DURATION,
    zIndex: 0
  }), /* @__PURE__ */ React.createElement(RemoveScroll, null, /* @__PURE__ */ React.createElement(TransitionComponent, {
    in: isOpen,
    timeout: TRANSITION_DURATION,
    onEnter: handleEnter,
    onExited: handleExited,
    onEntered: handleEntered
  }, /* @__PURE__ */ React.createElement(Wrapper, __spreadValues({
    ref,
    scrollBehavior,
    isCentered
  }, context.getModalWrapperProps()), /* @__PURE__ */ React.createElement(Modal$1, __spreadValues({
    size: context.size,
    scrollBehavior: context.scrollBehavior
  }, context.getModalProps(rest)), children)))))));
});
const Modal = Object.assign(ModalComponent, {
  Header: ModalHeader,
  Content: ModalContent,
  Section: ModalSection,
  Media: ModalSection,
  Footer: ModalFooter,
  CloseButton: ModalCloseButton,
  Title: ModalTitle,
  BackButton: ModalBackButton,
  ContinueButton: ModalContinueButton,
  CancelButton: ModalCancelButton
});
Modal.displayName = "Modal";

export { Modal };
//# sourceMappingURL=Modal.es.js.map
